import { z } from 'zod'
import { codigoDeMoeda, dataNominal, fracaoDeComissao, LIMITE_PADRAO_DO_SELETOR, limiteDoSeletor, listaDeUuidsNaUrl, uuid } from './comum.js'
import { dinheiroNaoNegativo, dinheiroPositivo, entradaDePagamentoDaContaPagar } from './contas-pagar.js'
import { gatilhoDeComissao, tipoDeComissao } from './cadastro.js'
import { tratamentoDeComissao } from './negocios.js'
export const statusDaParcelaDeComissao = z.enum([
  'aguardando_liberacao', 'liberada', 'doc_pendente', 'doc_registrado',
  'aguardando_aprovacao', 'aprovada', 'paga', 'cancelada', 'registrada',
])

export const linhaDeComissao = z.object({
  id: uuid,
  comissaoId: uuid,
  parceiroChave: z.string(),
  parceiroNome: z.string(),
  parceiroNatureza: z.enum(['PJ', 'PF']),
  pedidoId: uuid.nullable(),
  pedidoCodigo: z.string().nullable(),
  clienteNome: z.string().nullable(),
  tipo: tipoDeComissao,
  tratamento: tratamentoDeComissao,
  percentual: z.string().nullable(),
  baseValor: z.string().nullable(),
  numero: z.number().int(),
  totalParcelas: z.number().int(),
  valorBruto: z.string().nullable(),
  deducao: z.string().nullable(),
  liquido: z.string().nullable(),
  valorLiberado: z.string().nullable(),
  valorPendente: z.string().nullable(),
  vencimento: dataNominal.nullable(),
  competenciaGuiaRetencao: dataNominal.nullable(),
  vencimentoGuiaRetencao: dataNominal.nullable(),
  liberadaEm: dataNominal.nullable(),
  pagaEm: dataNominal.nullable(),
  liquidacaoId: uuid.nullable(),
  documentoAnexoId: uuid.nullable(),
  contasPagar: z.array(z.object({ id: uuid, codigo: z.string(), competencia: dataNominal })),
  progresso: z.object({
    recebidas: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    percentual: z.string(),
    denominador: z.string().nullable(),
  }).nullable(),
  status: statusDaParcelaDeComissao,
  gatilho: gatilhoDeComissao,
  moeda: codigoDeMoeda,
  /**
   * A parcela do cliente desta parcela de comissão (D-174, DS-618): ela libera quando esta parcela fica
   * quitada. Nula nas parcelas antigas, de liberação pro-rata, e na comissão sem parcela.
   */
  recebivel: z.object({
    id: uuid,
    numero: z.number().int(),
    total: z.number().int(),
    vencimento: dataNominal,
    quitadoEm: dataNominal.nullable(),
  }).nullable(),
  /**
   * DS-619: o crédito contra o parceiro ainda em aberto, na moeda da comissão. O próximo pagamento o abate:
   * paga-se o líquido menos ele. Nulo sem crédito, ou para quem não vê valores.
   */
  creditoDoParceiro: z.string().nullable().optional(),
  /** DS-619: a parte deste pagamento quitada com crédito contra o parceiro, e não com dinheiro. */
  compensadoNoPagamento: z.string().nullable().optional(),
})
export type LinhaDeComissao = z.infer<typeof linhaDeComissao>

export const agregadoDeComissoes = z.object({
  valor: z.string().nullable(),
  quantidade: z.number().int().nonnegative(),
})

export const capacidadesDeComissoes = z.object({
  podeEditar: z.boolean(),
  podeExportar: z.boolean(),
  veValoresFinanceiros: z.boolean(),
})
export type CapacidadesDeComissoes = z.infer<typeof capacidadesDeComissoes>

export const resumoDeExtratoDeComissao = z.object({
  pagoNoAno: z.string().nullable(),
  aPagar: z.string().nullable(),
  aguardandoCliente: z.string().nullable(),
  /** DS-619: a soma dos créditos contra o parceiro em aberto (saldo), na moeda base. */
  creditoEmAberto: z.string().nullable().optional(),
})

/** DS-619: a comissão paga a maior, como crédito contra o parceiro. */
export const creditoDoParceiro = z.object({
  id: uuid,
  parcelaComissaoId: uuid,
  pedidoCodigo: z.string().nullable(),
  valor: z.string().nullable(),
  saldo: z.string().nullable(),
  moeda: codigoDeMoeda,
  motivo: z.string(),
  /** Aberto com saldo; compensado quando os pagamentos o consumiram inteiro; encerrado por decisão, com motivo. */
  estado: z.enum(['aberto', 'compensado', 'encerrado']),
  criadoEm: dataNominal,
})
export type CreditoDoParceiro = z.infer<typeof creditoDoParceiro>

export const extratoDeComissao = z.object({
  parceiro: z.object({
    chave: z.string(),
    nome: z.string(),
    natureza: z.enum(['PJ', 'PF']),
    tipo: tipoDeComissao,
    regra: z.string().nullable(),
    /** A regra do nome acima, pelo MESMO critério (a da comissão mais recente); nula sem regra viva (D-136, DS-538). */
    regraId: uuid.nullable(),
    contraQuemEmitir: z.string(),
    /** A entidade emissora do "Emitir contra", para o nome abrir a ficha dela (D-136, DS-538). */
    emissoraId: uuid.nullable(),
    emailAviso: z.email().nullable(),
  }),
  resumo: resumoDeExtratoDeComissao,
  data: z.array(linhaDeComissao),
  /** DS-619: os créditos contra o parceiro, abertos primeiro. */
  creditos: z.array(creditoDoParceiro).optional(),
})
export type ExtratoDeComissao = z.infer<typeof extratoDeComissao>

export const entradaDeRegistroDeDocumentoDeComissao = z.object({ anexoId: uuid })
export type EntradaDeRegistroDeDocumentoDeComissao = z.infer<typeof entradaDeRegistroDeDocumentoDeComissao>

export const entradaDePagamentoDeComissao = entradaDePagamentoDaContaPagar.extend({
  /**
   * O DINHEIRO que sai: o líquido aprovado menos o crédito contra o parceiro em aberto (DS-619). Zero quando o
   * crédito cobre a comissão inteira.
   */
  valor: dinheiroNaoNegativo,
})
export type EntradaDePagamentoDeComissao = z.infer<typeof entradaDePagamentoDeComissao>

/** DS-619 (§28): a comissão paga é validada por um valor bruto menor; a diferença vira crédito contra o parceiro. */
export const entradaDeValidacaoDeComissao = z.object({
  valor: dinheiroPositivo,
  motivo: z.string().trim().min(3, 'Diga o motivo da validação a menor.'),
})
export type EntradaDeValidacaoDeComissao = z.infer<typeof entradaDeValidacaoDeComissao>

/** DS-619: sem próximo pagamento, o crédito se encerra por decisão explícita, com motivo. */
export const entradaDeEncerramentoDeCredito = z.object({
  motivo: z.string().trim().min(3, 'Diga o motivo do encerramento.'),
})
export type EntradaDeEncerramentoDeCredito = z.infer<typeof entradaDeEncerramentoDeCredito>

export const entradaDeConfiguracaoDaGuiaDeRetencao = z.object({
  competencia: dataNominal,
  vencimento: dataNominal,
}).refine(({competencia})=>competencia.endsWith('-01'),{
  path:['competencia'],message:'Informe a competência pelo primeiro dia do mês.',
})
export type EntradaDeConfiguracaoDaGuiaDeRetencao = z.infer<typeof entradaDeConfiguracaoDaGuiaDeRetencao>

export const entradaDePdfDeExtratoDeComissao = z.object({ parceiro: z.string().min(1) })
export const respostaDePdfDeExtratoDeComissao = z.object({
  jobId: uuid,
  estado: z.literal('enfileirado'),
})

export const FILA_PDF_EXTRATO_COMISSAO = 'dv-pdf-extrato-comissao'
export const jobDePdfDeExtratoDeComissao = z.object({
  entityId: uuid,
  parceiro: z.string().min(1),
  solicitadoPor: uuid,
  extrato: extratoDeComissao,
})
export type JobDePdfDeExtratoDeComissao = z.infer<typeof jobDePdfDeExtratoDeComissao>

export const regraDeComissao = z.object({
  id: uuid,
  nome: z.string(),
  prioridade: z.number().int(),
  condParceiroCapacidadeId: uuid.nullable(),
  condParceiroNome: z.string().nullable(),
  /**
   * A parte dona da capacidade de parceiro da condição: a Empresa, ou o Contato quando o
   * parceiro é pessoa física. É para onde o nome leva (D-136, DS-538); a capacidade não
   * tem ficha própria.
   */
  condParceiroEmpresaId: uuid.nullable(),
  condParceiroContatoId: uuid.nullable(),
  /**
   * O tipo que ESTÁ no cadastro do parceiro da condição, e não a condição de tipo da regra
   * (item 01 · DS-532). Com parceiro, é ele que vale; a tela o mostra como leitura e o compara
   * com `condTipoParceiroId` para acusar a regra que nunca se aplica.
   */
  condParceiroTipoParceiroId: uuid.nullable(),
  condParceiroTipoParceiroNome: z.string().nullable(),
  condTipoParceiroId: uuid.nullable(),
  condTipoParceiroNome: z.string().nullable(),
  condTipoEmpresaClienteId: uuid.nullable(),
  condTipoEmpresaClienteNome: z.string().nullable(),
  /** O setor do Tipo de Empresa da condição (D-32), para a mesma comparação com `condSetor`. */
  condTipoEmpresaClienteSetor: z.enum(['Público', 'Privado']).nullable(),
  condSetor: z.enum(['Público', 'Privado']).nullable(),
  condProdutoId: uuid.nullable(),
  condProdutoNome: z.string().nullable(),
  tipo: tipoDeComissao,
  tratamentoProposto: tratamentoDeComissao.nullable(),
  baseCalculo: z.string(),
  percentual: z.string().nullable(),
  valorFixo: z.string().nullable(),
  moeda: codigoDeMoeda.nullable(),
  gatilho: gatilhoDeComissao,
  contaContabilId: uuid.nullable(),
  contaContabilNome: z.string().nullable(),
  /** A âncora do Plano de Contas é o código (D-136, DS-538). */
  contaContabilCodigo: z.string().nullable(),
  centroCustoId: uuid.nullable(),
  centroCustoNome: z.string().nullable(),
  vigenciaDe: dataNominal,
  vigenciaAte: dataNominal.nullable(),
  ativa: z.boolean(),
  observacao: z.string().nullable(),
  uso: z.number().int(),
})
export type RegraDeComissao = z.infer<typeof regraDeComissao>

export const entradaDeRegraDeComissao = z.object({
  nome: z.string().trim().min(1),
  prioridade: z.number().int().positive(),
  condParceiroCapacidadeId: uuid.nullable().optional(),
  condTipoParceiroId: uuid.nullable().optional(),
  condTipoEmpresaClienteId: uuid.nullable().optional(),
  condSetor: z.enum(['Público', 'Privado']).nullable().optional(),
  condProdutoId: uuid.nullable().optional(),
  tipo: tipoDeComissao,
  tratamentoProposto: tratamentoDeComissao.nullable().optional(),
  baseCalculo: z.string().trim().min(1),
  percentual: fracaoDeComissao.nullish(),
  valorFixo: z.string().nullable().optional(),
  moeda: codigoDeMoeda.nullable().optional(),
  gatilho: gatilhoDeComissao,
  contaContabilId: uuid.nullable().optional(),
  centroCustoId: uuid.nullable().optional(),
  vigenciaDe: dataNominal,
  vigenciaAte: dataNominal.nullable().optional(),
  ativa: z.boolean(),
  observacao: z.string().trim().nullable().optional(),
})
export type EntradaDeRegraDeComissao = z.infer<typeof entradaDeRegraDeComissao>

export const exemploDeRegraDeComissao = z.object({
  parceiroCapacidadeId: uuid.nullable().optional(),
  tipoParceiroId: uuid.nullable().optional(),
  tipoEmpresaClienteId: uuid.nullable().optional(),
  setor: z.enum(['Público', 'Privado']).nullable().optional(),
  produtoId: uuid.nullable().optional(),
  data: dataNominal,
})

/**
 * O parceiro que o seletor da 10.4 oferece (item 03 · DS-529).
 *
 * A regra grava a CAPACIDADE (`cond_parceiro_capacidade_id`), então o valor do seletor é
 * `capacidadeId`. O tipo vem junto porque é ele que diz em que regra ampla o parceiro já
 * cai. E nenhum termo de taxa: a regra de comissão não precisa deles, e o seletor não é
 * janela para o cadastro do parceiro.
 */
export const parceiroDaRegra = z.object({
  capacidadeId: uuid,
  empresaId: uuid.nullable(),
  contatoId: uuid.nullable(),
  nome: z.string(),
  tipoParceiroId: uuid,
  tipoParceiroNome: z.string(),
})
export type ParceiroDaRegra = z.infer<typeof parceiroDaRegra>

/** `capacidades` resolve o valor gravado numa regra; o `limit` padrão é a página da tela. */
export const buscaDeParceirosDaRegra = z.object({
  q: z.string().max(120).optional(),
  limit: limiteDoSeletor.default(LIMITE_PADRAO_DO_SELETOR),
  capacidades: listaDeUuidsNaUrl.optional(),
})
export type BuscaDeParceirosDaRegra = z.infer<typeof buscaDeParceirosDaRegra>

export const respostaDeParceirosDaRegra = z.object({
  data: z.array(parceiroDaRegra),
  meta: z.object({ total: z.number().int() }),
})
export type RespostaDeParceirosDaRegra = z.infer<typeof respostaDeParceirosDaRegra>

/** Um item de catálogo das opções da 10.4. */
export const opcaoDeComissao = z.object({ id: uuid, nome: z.string() })
export type OpcaoDeComissao = z.infer<typeof opcaoDeComissao>

/** O tipo de cliente leva o setor do Tipo de Empresa (D-32): com ele escolhido, o setor é leitura. */
export const opcaoDeTipoDeClienteDaRegra = opcaoDeComissao.extend({ setor: z.enum(['Público', 'Privado']) })
export type OpcaoDeTipoDeClienteDaRegra = z.infer<typeof opcaoDeTipoDeClienteDaRegra>

/**
 * GET /regras-comissao/opcoes (item 01 · DS-532, EI-07).
 *
 * Sem `parceiros`: os dois seletores de parceiro da 10.4 buscam em GET /regras-comissao/parceiros
 * (DS-529), e o tipo do cadastro de cada parceiro vem no item de lá (`parceiroDaRegra`).
 */
export const opcoesDaRegraDeComissao = z.object({
  tiposParceiro: z.array(opcaoDeComissao),
  tiposCliente: z.array(opcaoDeTipoDeClienteDaRegra),
  produtos: z.array(opcaoDeComissao),
  contas: z.array(opcaoDeComissao),
  centros: z.array(opcaoDeComissao),
})
export type OpcoesDaRegraDeComissao = z.infer<typeof opcoesDaRegraDeComissao>
